<template>
	<div class="build-bar">
		<div class="build-bar-content sgnl-flex sgnl-flex-nowrap sgnl-gap-8-24 sgnl-align-center">
			<!-- Build section -->
			<div
				v-if="!hideBuildSection"
				class="build-bar-section sgnl-flex sgnl-align-center sgnl-gap-8"
			>
				<span class="build-bar-label sgnl-text-color-secondary sgnl-paragraph-s-medium">Build:</span>
				<div
					v-if="buildID !== undefined || activeBuildStatus"
					class="build-bar-id-slot"
				>
					<BuildIDDisplay
						v-if="buildID !== undefined"
						:buildID="buildID"
						:showClipboardButton="true"
					/>
					<SkeletonBlock
						v-else
						width="144px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
				</div>
				<div class="build-bar-status-active-slot">
					<span
						class="sgnl-paragraph-s-regular build-bar-status-text"
						:class="`sgnl-text-color-${buildStatusColor}`"
						data-test="project-status-value"
						>{{ buildStatus }}</span
					>
					<div class="build-bar-status-icon-slot">
						<v-tooltip
							v-if="buildAccessState?.error"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<WarningAlt16
									v-bind="props"
									class="build-bar-warning-icon"
								/>
							</template>
							<span>{{ buildAccessState.message }}</span>
						</v-tooltip>
						<v-progress-circular
							v-else-if="activeBuildStatus"
							indeterminate
							size="16"
							width="2"
							:color="buildStatusColor"
						/>
					</div>
					<v-btn
						v-if="buildStatus && isBuildActive(buildStatus)"
						variant="outlined"
						color="error"
						size="small"
						:disabled="!buildID"
						:loading="cancel.isCancellingBuild(buildID)"
						@click="cancelBuild"
					>
						Cancel
					</v-btn>
				</div>
			</div>
			<!-- Task section -->
			<div class="build-bar-section sgnl-flex sgnl-align-center sgnl-gap-8">
				<span class="build-bar-label sgnl-text-color-secondary sgnl-paragraph-s-medium">Task:</span>
				<div
					v-if="taskID !== undefined || activeTaskStatus"
					class="build-bar-id-slot"
				>
					<TaskIDDisplay
						v-if="taskID !== undefined"
						:taskID="taskID"
						:link="{ name: 'TaskDetail', params: { id: taskID } }"
						:showClipboardButton="true"
					/>
					<SkeletonBlock
						v-else
						width="144px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
				</div>
				<div class="build-bar-status-active-slot">
					<span
						class="sgnl-paragraph-s-regular build-bar-status-text"
						:class="`sgnl-text-color-${taskStatusColor}`"
						data-test="project-status-value"
						>{{ taskStatus }}</span
					>
					<div class="build-bar-status-icon-slot">
						<v-tooltip
							v-if="taskAccessState?.error"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<WarningAlt16
									v-bind="props"
									class="build-bar-warning-icon"
								/>
							</template>
							<span>{{ taskAccessState.message }}</span>
						</v-tooltip>
						<v-progress-circular
							v-else-if="activeTaskStatus"
							indeterminate
							size="16"
							width="2"
							:color="taskStatusColor"
						/>
					</div>
					<v-btn
						v-if="taskStatus && isTaskActive(taskStatus)"
						variant="outlined"
						color="error"
						size="small"
						:disabled="!taskID"
						:loading="cancel.isCancellingTask(taskID)"
						@click="cancelTask"
					>
						Cancel
					</v-btn>
				</div>
			</div>
		</div>
		<!-- Error messages -->
		<div
			v-if="dedupedFetchError"
			class="build-bar-error"
		>
			<ResourceErrorDisplay
				:view="buildErrorView"
				:showViewTask="showViewTask"
				:taskID="taskID"
				@retry="retryBoth"
			/>
		</div>
		<template v-else>
			<div
				v-if="buildErrorView"
				class="build-bar-error"
			>
				<ResourceErrorDisplay
					:view="buildErrorView"
					@retry="$emit('retry-build-output')"
				/>
			</div>
			<div
				v-if="buildFetchRetry"
				class="build-bar-error"
			>
				<ResourceErrorDisplay
					:view="buildFetchRetry"
					@retry="$emit('retry-build-output')"
				/>
			</div>
			<div
				v-if="taskErrorView"
				class="build-bar-error"
			>
				<ResourceErrorDisplay
					:view="taskErrorView"
					:showViewTask="showViewTask"
					:taskID="taskID"
					@retry="$emit('retry-task-output')"
				/>
			</div>
			<div
				v-if="taskFetchRetry"
				class="build-bar-error"
			>
				<ResourceErrorDisplay
					:view="taskFetchRetry"
					:showViewTask="showViewTask"
					:taskID="taskID"
					@retry="$emit('retry-task-output')"
				/>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent, inject, PropType, toRef } from "vue";
import { CancelFeedbackKey, CANCEL_FEEDBACK_NOOP } from "@/composables/useCancelWithFeedback";
import { useStuckResourceMessage } from "@/composables/useStuckResourceMessage";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";

// Types
import { isTaskActive, isTaskFail, Task, TaskStatus, TaskStatusE } from "@/types/api/tasks";

// Utilities
import * as util from "@/js/utilities";

// Components
import TaskIDDisplay from "@/components/TaskIDDisplay.vue";
import BuildIDDisplay from "@/components/BuildIDDisplay.vue";
import ResourceErrorDisplay from "@/components/Common/ResourceErrorDisplay.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { RemoteAccessState } from "@/types/general";
import { Build, BuildStatus, isBuildActive, isBuildFail } from "@/types/api/builds";

export default defineComponent({
	name: "BuildBar",
	components: {
		TaskIDDisplay,
		BuildIDDisplay,
		ResourceErrorDisplay,
		SkeletonBlock,
		WarningAlt16,
	},
	emits: ["cancel-task", "cancel-build", "retry-task-output", "retry-build-output"],
	props: {
		taskID: { type: String },
		taskStatus: { type: String as PropType<TaskStatus> },
		taskAccessState: { type: Object as PropType<RemoteAccessState> },
		activeTask: { type: Object as PropType<undefined | Partial<Task>> },
		buildID: { type: String },
		buildStatus: { type: String as PropType<BuildStatus> },
		activeBuild: { type: Object as PropType<undefined | Partial<Build>> },
		buildAccessState: { type: Object as PropType<RemoteAccessState> },
		hideBuildSection: { type: Boolean, default: false },
		showViewTask: { type: Boolean, default: false },
	},
	setup(props) {
		const cancel = inject(CancelFeedbackKey, null);
		if (!cancel && !import.meta.env.PROD) {
			console.warn("[BuildBar] No CancelFeedbackKey provider found in ancestor chain.");
		}
		const taskStuckMessage = useStuckResourceMessage(toRef(props, "activeTask"), "task");
		const buildStuckMessage = useStuckResourceMessage(toRef(props, "activeBuild"), "build");
		return {
			cancel: cancel ?? CANCEL_FEEDBACK_NOOP,
			isTaskActive,
			TaskStatusE,
			isBuildActive,
			taskStuckMessage,
			buildStuckMessage,
		};
	},
	computed: {
		taskStatusColor(): string {
			return util.taskStatusColor(this.taskStatus);
		},
		buildStatusColor(): string {
			return util.buildStatusColor(this.buildStatus);
		},
		activeTaskStatus(): boolean {
			return this.taskStatus ? isTaskActive(this.taskStatus) : false;
		},
		activeBuildStatus(): boolean {
			return this.buildStatus ? isBuildActive(this.buildStatus) : false;
		},
		taskStatusMessageOnError: function (): string | null {
			if (this.activeTask?.Status && !isTaskFail(this.activeTask.Status)) {
				return null;
			}
			const statusTransition =
				this.activeTask?.StatusTransitions &&
				this.activeTask.StatusTransitions.filter((st) => st.Status == this.activeTask?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}

			return statusTransition?.Message ?? null;
		},
		buildStatusMessageOnError: function (): string | null {
			if (this.activeBuild?.Status && !isBuildFail(this.activeBuild.Status)) {
				return null;
			}
			const statusTransition =
				this.activeBuild?.StatusTransitions &&
				this.activeBuild.StatusTransitions.filter((st) => st.Status == this.activeBuild?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}
			return statusTransition?.Message ?? null;
		},
		buildErrorView(): util.ResourceErrorView {
			if (this.hideBuildSection) return null;
			return util.resolveResourceError(
				this.buildStatusMessageOnError,
				this.buildAccessState,
				this.buildStuckMessage,
			);
		},
		taskErrorView(): util.ResourceErrorView {
			return util.resolveResourceError(
				this.taskStatusMessageOnError,
				this.taskAccessState,
				this.taskStuckMessage,
			);
		},
		buildFetchRetry(): util.ResourceErrorView {
			if (this.hideBuildSection) return null;
			return util.resolveFetchRetry(this.buildStatusMessageOnError, this.buildAccessState);
		},
		taskFetchRetry(): util.ResourceErrorView {
			return util.resolveFetchRetry(this.taskStatusMessageOnError, this.taskAccessState);
		},
		dedupedFetchError(): boolean {
			return util.isDedupedFetchError(this.buildErrorView, this.taskErrorView);
		},
	},
	methods: {
		cancelTask() {
			this.$emit("cancel-task");
		},
		cancelBuild() {
			this.$emit("cancel-build");
		},
		retryBoth() {
			this.$emit("retry-build-output");
			this.$emit("retry-task-output");
		},
	},
});
</script>

<style scoped>
.build-bar {
	background: var(--sgnl-default-secondary-background-mono);
	border-radius: 0 0 var(--sgnl-border-radius-6) var(--sgnl-border-radius-6);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	font-size: var(--sgnl-font-size-paragraph-s);
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.build-bar-label {
	white-space: nowrap;
}

.build-bar-error {
	display: flex;
	align-items: baseline;
	gap: var(--sgnl-space-8);
	font-size: var(--sgnl-font-size-paragraph-xs);
}

.build-bar-section {
	flex-wrap: nowrap;
}

.build-bar-id-slot {
	display: flex;
	align-items: center;
	min-width: 144px;
	flex-shrink: 0;
}

.build-bar-id-slot :deep(.copy-id-chip) {
	min-width: 100%;
}

.build-bar-status-active-slot {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
	min-width: 190px;
	flex-shrink: 0;
	white-space: nowrap;
}

.build-bar-status-text {
	display: inline-block;
	min-width: 80px;
}

.build-bar-status-icon-slot {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.build-bar-warning-icon {
	color: var(--sgnl-alert-warning-icon);
	flex-shrink: 0;
}

@media (max-width: 599px) {
	.build-bar-content {
		flex-direction: column;
		align-items: flex-start;
	}

	.build-bar :deep(.v-btn) {
		width: auto;
	}

	.build-bar-id-slot,
	.build-bar-status-active-slot {
		min-width: 0;
	}
}
</style>
